import { Chip } from "@saviynt/design-system";
import { useIntl } from "react-intl";

interface ExpiryBadgeProps {
  expiryDate: Date | null;
}

/**
 * Displays a color-coded chip indicating certificate expiry status.
 *
 * - neutral (>90 days): "Expires: {formatted date}"
 * - warning (≤90 days): "Expires in {N} days"
 * - critical (≤30 days): "Expires in {N} days — renewal required"
 * - critical (expired): "Certificate expired on {date}"
 * - null: "Expiry unknown" (neutral)
 */
export default function ExpiryBadge({ expiryDate }: ExpiryBadgeProps) {
  const intl = useIntl();

  if (!expiryDate) {
    return (
      <span role="status" aria-live="polite">
        <Chip
          label={intl.formatMessage({
            id: "9pfirp",
            defaultMessage: "Expiry unknown",
            description: "Certificate expiry badge when date cannot be parsed",
          })}
          color="neutral"
          size="small"
        />
      </span>
    );
  }

  const now = new Date();
  const diffMs = expiryDate.getTime() - now.getTime();
  const daysUntilExpiry = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  // Expired
  if (daysUntilExpiry <= 0) {
    const formattedDate = intl.formatDate(expiryDate, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return (
      <span role="status" aria-live="polite">
        <Chip
          label={intl.formatMessage(
            {
              id: "h3lNt9",
              defaultMessage: "Certificate expired on {date}",
              description:
                "Certificate expiry badge when certificate has expired",
            },
            { date: formattedDate },
          )}
          color="critical"
          size="small"
        />
      </span>
    );
  }

  // Critical (≤30 days)
  if (daysUntilExpiry <= 30) {
    return (
      <span role="status" aria-live="polite">
        <Chip
          label={intl.formatMessage(
            {
              id: "SU2d2i",
              defaultMessage: "Expires in {days} days — renewal required",
              description:
                "Certificate expiry badge when expiring within 30 days",
            },
            { days: daysUntilExpiry },
          )}
          color="critical"
          size="small"
        />
      </span>
    );
  }

  // Warning (≤90 days)
  if (daysUntilExpiry <= 90) {
    return (
      <span role="status" aria-live="polite">
        <Chip
          label={intl.formatMessage(
            {
              id: "5SfGRi",
              defaultMessage: "Expires in {days} days",
              description:
                "Certificate expiry badge when expiring within 90 days",
            },
            { days: daysUntilExpiry },
          )}
          color="warning"
          size="small"
        />
      </span>
    );
  }

  // Neutral (>90 days)
  const formattedDate = intl.formatDate(expiryDate, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return (
    <span role="status" aria-live="polite">
      <Chip
        label={intl.formatMessage(
          {
            id: "0UUabA",
            defaultMessage: "Expires: {date}",
            description:
              "Certificate expiry badge when expiring in more than 90 days",
          },
          { date: formattedDate },
        )}
        color="neutral"
        size="small"
      />
    </span>
  );
}
